/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  LabelPlacement,
  MediaTransmissionStatus,
  TransmissionLabelFormatVariant,
} from "../enums";
import { IConfigurationBrandingModel, IMediaModel } from "../models";

import { TimeHelper } from "./timeHelper";

export interface ITransmissionLabelFormatConfig {
  dateFormat: string;
  timeFormat: string;
  separator: string;
}

export interface IMediaTransmissionStatusLabel {
  transmissionStatus: MediaTransmissionStatus;
  label: string;
}

export class LabelHelper {
  private static readonly DEFAULT_DATE_FORMAT = "D. MMM";

  private static readonly DEFAULT_TIME_FORMAT = "HH:mm";

  private static readonly DEFAULT_SEPARATOR = " ";

  static getTransmissionFormatConfig(
    branding: IConfigurationBrandingModel,
  ): ITransmissionLabelFormatConfig {
    return {
      dateFormat:
        branding.AppTransmissionLabelDateFormat ||
        LabelHelper.DEFAULT_DATE_FORMAT,
      timeFormat:
        branding.AppTransmissionLabelTimeFormat ||
        LabelHelper.DEFAULT_TIME_FORMAT,
      separator:
        branding.AppTransmissionLabelDateTimeSeparator ??
        LabelHelper.DEFAULT_SEPARATOR,
    };
  }

  static isEdgePaddingEnabled(branding: IConfigurationBrandingModel): boolean {
    const value = branding.AppLabelEdgePadding;

    if (value === false || value === "false") {
      return false;
    }

    return true;
  }

  static getLabelPlacementAttribute(
    placement: LabelPlacement,
  ): string | undefined {
    switch (placement) {
      case LabelPlacement.TopLeft:
        return "TOP_LEFT";
      case LabelPlacement.TopRight:
        return "TOP_RIGHT";
      case LabelPlacement.BottomLeft:
        return "BOTTOM_LEFT";
      case LabelPlacement.BottomRight:
        return "BOTTOM_RIGHT";
      default:
        return undefined;
    }
  }

  static isTransmissionStartToday(
    startDateTime: Date | number | string,
  ): boolean {
    return (
      TimeHelper.getDateKey(startDateTime) === TimeHelper.getCurrentDateKey()
    );
  }

  static formatTransmission(
    startDateTime: Date | number | string,
    status: MediaTransmissionStatus.UPCOMING | MediaTransmissionStatus.TODAY,
    config: ITransmissionLabelFormatConfig,
    options: {
      variant?: TransmissionLabelFormatVariant;
      todayLabel: string;
    },
  ): string {
    const { variant = TransmissionLabelFormatVariant.DateTime, todayLabel } =
      options;
    const timePart = TimeHelper.format(
      startDateTime,
      config.timeFormat || LabelHelper.DEFAULT_TIME_FORMAT,
    );

    if (variant === TransmissionLabelFormatVariant.Time) {
      return timePart;
    }

    if (status === MediaTransmissionStatus.TODAY) {
      return `${todayLabel}${config.separator}${timePart}`;
    }

    const datePart = TimeHelper.format(
      startDateTime,
      config.dateFormat || LabelHelper.DEFAULT_DATE_FORMAT,
    );

    return `${datePart}${config.separator}${timePart}`;
  }

  static getMediaTransmissionStatusLabel(
    media: IMediaModel | undefined,
    options: {
      formatConfig: ITransmissionLabelFormatConfig;
      formatVariant?: TransmissionLabelFormatVariant;
      endedLabel: string;
      liveLabel: string;
      todayLabel: string;
    },
  ): IMediaTransmissionStatusLabel | null {
    if (!media || !media.StartDateTime) {
      return null;
    }

    const currentDate = Date.now();

    if (
      media.EndDateTime &&
      TimeHelper.isBefore(media.EndDateTime, currentDate)
    ) {
      return {
        transmissionStatus: MediaTransmissionStatus.ENDED,
        label: options.endedLabel,
      };
    }

    if (
      TimeHelper.isCurrentBetween(media.StartDateTime, media.EndDateTime) ||
      (!media.EndDateTime &&
        !TimeHelper.isBefore(currentDate, media.StartDateTime))
    ) {
      return {
        transmissionStatus: MediaTransmissionStatus.LIVE,
        label: options.liveLabel,
      };
    }

    if (TimeHelper.isBefore(currentDate, media.StartDateTime)) {
      if (LabelHelper.isTransmissionStartToday(media.StartDateTime)) {
        return {
          transmissionStatus: MediaTransmissionStatus.TODAY,
          label: LabelHelper.formatTransmission(
            media.StartDateTime,
            MediaTransmissionStatus.TODAY,
            options.formatConfig,
            {
              variant: options.formatVariant,
              todayLabel: options.todayLabel,
            },
          ),
        };
      }

      return {
        transmissionStatus: MediaTransmissionStatus.UPCOMING,
        label: LabelHelper.formatTransmission(
          media.StartDateTime,
          MediaTransmissionStatus.UPCOMING,
          options.formatConfig,
          {
            variant: options.formatVariant,
            todayLabel: options.todayLabel,
          },
        ),
      };
    }

    return null;
  }
}
